import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Users, FileText, Ticket as TicketIcon, ShoppingBag, ClipboardList, Key, BarChart3, Bell, Webhook as WebhookIcon, Megaphone, Loader2, Package } from 'lucide-react';
import StaffActivityStrip from '@/components/StaffActivityStrip';
import NotificationPrefsDialog from '@/components/NotificationPrefsDialog';
import NotificationBell from '@/components/NotificationBell';
import { getPermissions } from '@/lib/permissions';

// Lazy-load the heavy tab components — analytics + marketing pull in recharts
// and multiple dialogs that aren't needed unless the admin actually clicks
// the tab. Cuts the initial admin bundle by ~40%.
const AdminServicesTab = lazy(() => import('@/components/AdminServicesTab'));
const AdminUsersTab = lazy(() => import('@/components/AdminUsersTab'));
const AdminInvoicesTab = lazy(() => import('@/components/AdminInvoicesTab'));
const AdminTicketsTab = lazy(() => import('@/components/AdminTicketsTab'));
const AdminQuotesTab = lazy(() => import('@/components/AdminQuotesTab'));
const AdminCredentialsTab = lazy(() => import('@/components/AdminCredentialsTab'));
const AdminAnalyticsTab = lazy(() => import('@/components/AdminAnalyticsTab'));
const AdminWebhooksTab = lazy(() => import('@/components/AdminWebhooksTab'));
const AdminMarketingTab = lazy(() => import('@/components/AdminMarketingTab'));
const AdminPresetItemsTab = lazy(() => import('@/components/AdminPresetItemsTab'));

const TabFallback = () => (
  <div className="py-16 flex items-center justify-center" data-testid="admin-tab-loading">
    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (!['admin', 'technician', 'accountant'].includes(response.data.role)) {
        toast.error('Staff access required');
        navigate('/dashboard');
        return;
      }
      // Technicians get redirected to their dedicated workspace.
      if (response.data.role === 'technician') {
        navigate('/tech', { replace: true, state: { user: response.data } });
        return;
      }
      
      setUser(response.data);
      fetchData();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, invoicesRes, ticketsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/invoices`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tickets`, { withCredentials: true })
      ]);

      setUsers(usersRes.data);
      setInvoices(invoicesRes.data);
      setTickets(ticketsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <AdminBody
        user={user}
        users={users}
        invoices={invoices}
        tickets={tickets}
        navigate={navigate}
        fetchData={fetchData}
      />
    </div>
  );
};

// Body extracted to a separate component so the permissions object is computed
// inside it (after `user` is non-null). Keeps the parent's branching simple.
const AdminBody = ({ user, users, invoices, tickets, navigate, fetchData }) => {
  const perms = getPermissions(user);
  const defaultTab = perms.visibleTabs[0] || 'analytics';
  const [showPrefs, setShowPrefs] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex h-16 items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <div className="font-black text-xl uppercase tracking-tight">
                NEXT<span className="text-primary">/</span>GEN ADMIN
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPrefs(true)} data-testid="admin-prefs-btn" title="Notification preferences">
                <Bell className="w-4 h-4" />
              </Button>
              <NotificationBell testIdPrefix="admin" />
            </div>
          </div>
        </div>
      </header>
      <NotificationPrefsDialog open={showPrefs} onClose={() => setShowPrefs(false)} />

      <div className="container mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-12">
        <h1 className="text-2xl sm:text-4xl font-black uppercase mb-6 sm:mb-12">ADMIN PANEL</h1>

        <StaffActivityStrip />

        <Tabs defaultValue={defaultTab} className="space-y-8">
          <TabsList className="bg-card border border-border flex-wrap h-auto p-1">
            {perms.tabs.analytics && (
              <TabsTrigger value="analytics" data-testid="analytics-tab" className="text-xs sm:text-sm">
                <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Analytics</span>
              </TabsTrigger>
            )}
            {perms.tabs.users && (
              <TabsTrigger value="users" data-testid="users-tab" className="text-xs sm:text-sm">
                <Users className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Users</span>
              </TabsTrigger>
            )}
            {perms.tabs.services && (
              <TabsTrigger value="services" data-testid="services-tab" className="text-xs sm:text-sm">
                <ShoppingBag className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Services</span>
              </TabsTrigger>
            )}
            {perms.tabs.quotes && (
              <TabsTrigger value="quotes" data-testid="quotes-tab" className="text-xs sm:text-sm">
                <ClipboardList className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Quotes</span>
              </TabsTrigger>
            )}
            {perms.tabs.invoices && (
              <TabsTrigger value="invoices" data-testid="invoices-tab" className="text-xs sm:text-sm">
                <FileText className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Invoices</span>
              </TabsTrigger>
            )}
            {perms.tabs.tickets && (
              <TabsTrigger value="tickets" data-testid="tickets-tab" className="text-xs sm:text-sm">
                <TicketIcon className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Tickets</span>
              </TabsTrigger>
            )}
            {perms.tabs.credentials && (
              <TabsTrigger value="credentials" data-testid="credentials-tab" className="text-xs sm:text-sm">
                <Key className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Credentials</span>
              </TabsTrigger>
            )}
            {perms.tabs.webhooks && (
              <TabsTrigger value="webhooks" data-testid="webhooks-tab" className="text-xs sm:text-sm">
                <WebhookIcon className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Webhooks</span>
              </TabsTrigger>
            )}
            {perms.tabs.marketing && (
              <TabsTrigger value="marketing" data-testid="marketing-tab" className="text-xs sm:text-sm">
                <Megaphone className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Marketing</span>
              </TabsTrigger>
            )}
            {perms.tabs.presets && (
              <TabsTrigger value="presets" data-testid="presets-tab" className="text-xs sm:text-sm">
                <Package className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Presets</span>
              </TabsTrigger>
            )}
          </TabsList>

          {perms.tabs.analytics && (
            <TabsContent value="analytics">
              <Suspense fallback={<TabFallback />}><AdminAnalyticsTab /></Suspense>
            </TabsContent>
          )}

          {perms.tabs.users && (
            <TabsContent value="users">
              <Suspense fallback={<TabFallback />}>
                <AdminUsersTab users={users} onRefresh={fetchData} />
              </Suspense>
            </TabsContent>
          )}

          {perms.tabs.services && (
            <TabsContent value="services">
              <Suspense fallback={<TabFallback />}><AdminServicesTab /></Suspense>
            </TabsContent>
          )}

          {perms.tabs.quotes && (
            <TabsContent value="quotes">
              <Suspense fallback={<TabFallback />}>
                <AdminQuotesTab users={users} onRefresh={fetchData} permissions={perms} />
              </Suspense>
            </TabsContent>
          )}

          {perms.tabs.invoices && (
            <TabsContent value="invoices">
              <Suspense fallback={<TabFallback />}>
                <AdminInvoicesTab
                  invoices={invoices}
                  users={users}
                  onRefresh={fetchData}
                />
              </Suspense>
            </TabsContent>
          )}

          {perms.tabs.tickets && (
            <TabsContent value="tickets">
              <Suspense fallback={<TabFallback />}>
                <AdminTicketsTab
                  tickets={tickets}
                  users={users}
                  onRefresh={fetchData}
                  permissions={perms}
                  currentUserEmail={user?.email}
                />
              </Suspense>
            </TabsContent>
          )}

          {perms.tabs.credentials && (
            <TabsContent value="credentials">
              <Suspense fallback={<TabFallback />}>
                <AdminCredentialsTab users={users} />
              </Suspense>
            </TabsContent>
          )}

          {perms.tabs.webhooks && (
            <TabsContent value="webhooks">
              <Suspense fallback={<TabFallback />}><AdminWebhooksTab /></Suspense>
            </TabsContent>
          )}

          {perms.tabs.marketing && (
            <TabsContent value="marketing">
              <Suspense fallback={<TabFallback />}><AdminMarketingTab /></Suspense>
            </TabsContent>
          )}

          {perms.tabs.presets && (
            <TabsContent value="presets">
              <Suspense fallback={<TabFallback />}><AdminPresetItemsTab /></Suspense>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </>
  );
};

export default AdminDashboard;
